import { useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Checklist } from '@eneco/components/ui/Checklist/Checklist';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { Checkbox, NotificationBox, TextLink } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import { OxxioNewDeliveryDateStepRendering } from '@sitecore/types/OxxioNewDeliveryDateStep';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowConsumerContextEnergy } from '../../../context';
import { useFlowNavigation } from '../../../shared/hooks';

interface FormFields {
  consentConsiderationPeriod: boolean;
}

export const DeliveryDateStep = () => {
  const { DeliveryDateStep: { fields: textualData } = {} } = usePlaceholderContent<{
    DeliveryDateStep: OxxioNewDeliveryDateStepRendering;
  }>();

  const { useFlowSelector } = useFlowHooks<FlowConsumerContextEnergy>();
  const flowState = useFlowSelector(state => state);
  const { updateContextAndGoToNextStep } = useFlowNavigation();
  const {
    context: { consentConsiderationPeriod },
  } = flowState;

  const formSchema = yup.object({
    consentConsiderationPeriod: yup
      .boolean()
      .required()
      .test('true', textualData?.deliveryDate?.isUrgentFormField?.value.requiredMessage ?? '', value => !!value),
  });

  const {
    formState: { errors, isSubmitSuccessful },
    getValues,
    handleSubmit,
    register,
  } = useForm<FormFields>({
    mode: 'onTouched',
    resolver: typedYupResolver<FormFields>(formSchema),
    defaultValues: {
      consentConsiderationPeriod,
    },
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const { consentConsiderationPeriod } = getValues();

      updateContextAndGoToNextStep({ consentConsiderationPeriod });
    }
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        <Header.Text>{textualData?.content?.text?.value}</Header.Text>
      </Header>

      {textualData?.data?.usp1Text?.value && (
        <Checklist>
          {textualData?.data?.usp1Text?.value && (
            <Checklist.Item key={1} gap="2">
              <RichText html={textualData?.data?.usp1Text?.value} />
            </Checklist.Item>
          )}
          {textualData?.data?.usp2Text?.value && (
            <Checklist.Item key={2} gap="2">
              <RichText html={textualData?.data?.usp2Text?.value} />
            </Checklist.Item>
          )}
          {textualData?.data?.usp3Text?.value && (
            <Checklist.Item key={3} gap="2">
              <RichText html={textualData?.data?.usp3Text?.value} />
            </Checklist.Item>
          )}
        </Checklist>
      )}

      {textualData?.content?.notification?.value?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.value?.variant}
          title={textualData?.content?.notification?.value?.title}
          text={<RichText html={textualData?.content?.notification?.value?.content} />}
        />
      )}
      <Checkbox
        {...register('consentConsiderationPeriod')}
        error={errors['consentConsiderationPeriod']?.message}
        label={textualData?.deliveryDate?.isUrgentFormField?.value?.label ?? ''}
      />

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>

      {textualData?.data?.normalSalesFlowLink?.value && (
        <TextLink
          href={textualData?.data?.normalSalesFlowLink?.value?.href}
          target={textualData?.data?.normalSalesFlowLink?.value?.target}
          emphasis="high">
          {textualData?.data?.normalSalesFlowLink?.value?.text}
        </TextLink>
      )}
    </Layout.Content>
  );
};
